import { defineStore } from 'pinia';
import Vue, { ref } from 'vue';
import { useAdvisorStore } from './advisorStore.js';

// TODO: not sure if this is the cleanest to fetch the env var
// but other options were failing
const { AI_ENDPOINT_URL } = JSON.parse(
    document.getElementById('JS_BOOTSTRAP').textContent,
);

export const useJobsStore = defineStore('jobs', () => {
  const jobs = ref([]);
  const isFetching = ref(false);
  const hasFetched = ref(false);
  const jobsBeingUpdated = ref([]);
  const runsBeingFetched = ref([]);

  // Helpers
  function upsertJob(job) {
    const index = jobs.value.findIndex(j => Number(j.id) === Number(job.id));
    const withRuns = {
      runs: [],
      ...job,
    };

    if (index === -1) {
      jobs.value.push(withRuns);
    } else {
      jobs.value[index] = {
        ...jobs.value[index],
        ...withRuns,
      };
    }
  }

  function updateJobRuns(jobId, runs) {
    const id = Number(jobId);
    const index = jobs.value.findIndex(j => Number(j.id) === id);

    if (index === -1) {
      return;
    }

    const current = jobs.value[index] || {};

    // Replace the array element itself so Vue 2 *definitely* reacts,
    // and ensure `runs` exists even if it wasn't there before.
    Vue.set(jobs.value, index, {
      ...current,
      runs,
    });
  }

  function getJobById(id) {
    const n = Number(id);
    return jobs.value.find(j => j.id === n);
  }

  function isUpdatingJob(jobId) {
    return jobsBeingUpdated.value.includes(jobId);
  }

  function isFetchingRuns(jobId) {
    return runsBeingFetched.value.includes(jobId);
  }

  // API
  async function createJob(title, prompt, cron, recipients = []) {
    try {
      const advisorStore = useAdvisorStore();
      const res = await fetch(`${AI_ENDPOINT_URL}/ai/jobs/`, {
        method: 'POST',
        headers: advisorStore.headers,
        credentials: 'include',
        body: JSON.stringify({
          title,
          prompt,
          cron,
          recipients, // optional, backend auto-includes creator email
        }),
      });

      if (!res.ok) {
        console.error(`Error creating job`);
        return null;
      }

      const job = await res.json();
      await fetchJobs();
      return job;
    } catch (error) {
      console.error(error, 'Failed to create job');
    }
  }

  async function fetchJobs() {
    isFetching.value = true;

    try {
      const advisorStore = useAdvisorStore();
      const res = await fetch(`${AI_ENDPOINT_URL}/ai/jobs/`, {
        method: 'GET',
        headers: advisorStore.headers,
        credentials: 'include',
      });

      if (!res.ok) {
        console.error(`Error fetching jobs`);
        return null;
      }

      const data = await res.json();
      jobs.value = (data.jobs || []).map(job => ({
        ...job,
        runs: job.runs || [], // Ensure runs exists
      }));
    } catch (error) {
      console.error(error, 'Failed to load jobs');
    } finally {
      hasFetched.value = true;
      isFetching.value = false;
    }
  }

  // API: fetch a single job and upsert into jobs[]
  async function fetchSingleJob(jobId) {
    try {
      const advisorStore = useAdvisorStore();
      const res = await fetch(`${AI_ENDPOINT_URL}/ai/jobs/${jobId}/`, {
        method: 'GET',
        headers: advisorStore.headers,
        credentials: 'include',
      });

      if (!res.ok) {
        console.error(`Error fetching job ${jobId}`);
        return null;
      }

      const job = await res.json();
      upsertJob(job);
      return job;
    } catch (error) {
      console.error(error, `Failed to load job ${jobId}`);
      return null;
    }
  }

  async function updateJob(jobId, updates = {}) {
    if (jobsBeingUpdated.value.includes(jobId)) {
      console.error(`Runs for job ${jobId} are already being fetched.`);
      return;
    }

    jobsBeingUpdated.value.push(jobId);

    try {
      const advisorStore = useAdvisorStore();
      const payload = {};

      for (const [key, value] of Object.entries(updates)) {
        if (value !== undefined) {
          payload[key] = value;
        }
      }

      const res = await fetch(`${AI_ENDPOINT_URL}/ai/jobs/${jobId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          ...advisorStore.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        console.error(`Error updating job ${jobId}`);
        return null;
      }

      const udpatedJob = await res.json();
      upsertJob(udpatedJob);
      return udpatedJob;
    } catch (error) {
      console.error(error, `Failed to update job ${jobId}`);
      return null;
    } finally {
      jobsBeingUpdated.value = jobsBeingUpdated.value.filter(j => j !== jobId);
    }
  }

  async function deleteJob(jobId) {
    if (jobsBeingUpdated.value.includes(jobId)) {
      console.error(`Job ${jobId} already being updated.`);
      return;
    }

    if (!jobId) {
      return;
    }

    jobsBeingUpdated.value.push(jobId);

    try {
      const advisorStore = useAdvisorStore();
      const res = await fetch(`${AI_ENDPOINT_URL}/ai/jobs/${jobId}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          ...advisorStore.headers,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        console.error(`Error deleting job ${jobId}`);
        return null;
      }

      return;
    } catch (error) {
      console.error(error, `Failed to delete job ${jobId}`);
      return null;
    } finally {
      jobsBeingUpdated.value = jobsBeingUpdated.value.filter(j => j !== jobId);
    }
  }

  async function runJob(jobId) {
    try {
      const advisorStore = useAdvisorStore();
      const res = await fetch(`${AI_ENDPOINT_URL}/ai/jobs/${jobId}/run/`, {
        method: 'POST',
        headers: advisorStore.headers,
        credentials: 'include',
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to run job');
      }

      const runResult = await res.json();
      fetchRuns(jobId);
      return runResult;
    } catch (e) {
      alert(e.message || String(e));
    }
  }

  async function fetchRuns(jobId) {
    if (runsBeingFetched.value.includes(jobId)) {
      console.error(`Runs for job ${jobId} area already being fetched.`);
      return;
    }

    runsBeingFetched.value.push(jobId);

    try {
      const advisorStore = useAdvisorStore();
      const res = await fetch(`${AI_ENDPOINT_URL}/ai/jobs/${jobId}/runs/`, {
        method: 'GET',
        headers: advisorStore.headers,
        credentials: 'include',
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to run job');
      }

      const result = await res.json();
      updateJobRuns(result.job_id, result.runs);
      return result;
    } catch (e) {
      alert(e.message || String(e));
    } finally {
      runsBeingFetched.value = runsBeingFetched.value.filter(j => j !== jobId);
    }
  }

  return {
    jobs,
    isFetching,
    hasFetched,
    runsBeingFetched,
    jobsBeingUpdated,

    createJob,
    fetchJobs,
    fetchSingleJob,
    fetchRuns,
    getJobById,
    updateJob,
    deleteJob,
    runJob,
    isFetchingRuns,
    isUpdatingJob,
  };
});
